import React, { useState, useMemo } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Tooltip } from 'react-tooltip';
import { MapPin, Minimize2, Maximize2, DollarSign, TrendingUp, BarChart3 } from 'lucide-react';

const US_STATES_TOPO = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// FIPS code → state abbreviation
const FIPS_TO_STATE = {
  '01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT','10':'DE',
  '11':'DC','12':'FL','13':'GA','15':'HI','16':'ID','17':'IL','18':'IN','19':'IA',
  '20':'KS','21':'KY','22':'LA','23':'ME','24':'MD','25':'MA','26':'MI','27':'MN',
  '28':'MS','29':'MO','30':'MT','31':'NE','32':'NV','33':'NH','34':'NJ','35':'NM',
  '36':'NY','37':'NC','38':'ND','39':'OH','40':'OK','41':'OR','42':'PA','44':'RI',
  '45':'SC','46':'SD','47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA',
  '54':'WV','55':'WI','56':'WY'
};

const STATE_NAMES = {
  'AL':'Alabama','AK':'Alaska','AZ':'Arizona','AR':'Arkansas','CA':'California',
  'CO':'Colorado','CT':'Connecticut','DE':'Delaware','DC':'District of Columbia',
  'FL':'Florida','GA':'Georgia','HI':'Hawaii','ID':'Idaho','IL':'Illinois',
  'IN':'Indiana','IA':'Iowa','KS':'Kansas','KY':'Kentucky','LA':'Louisiana',
  'ME':'Maine','MD':'Maryland','MA':'Massachusetts','MI':'Michigan','MN':'Minnesota',
  'MS':'Mississippi','MO':'Missouri','MT':'Montana','NE':'Nebraska','NV':'Nevada',
  'NH':'New Hampshire','NJ':'New Jersey','NM':'New Mexico','NY':'New York',
  'NC':'North Carolina','ND':'North Dakota','OH':'Ohio','OK':'Oklahoma','OR':'Oregon',
  'PA':'Pennsylvania','RI':'Rhode Island','SC':'South Carolina','SD':'South Dakota',
  'TN':'Tennessee','TX':'Texas','UT':'Utah','VT':'Vermont','VA':'Virginia',
  'WA':'Washington','WV':'West Virginia','WI':'Wisconsin','WY':'Wyoming'
};

const METRICS = [
  { key: 'gross_revenue', label: 'Gross Revenue', icon: DollarSign, format: 'currency' },
  { key: 'net_revenue', label: 'Net Revenue', icon: TrendingUp, format: 'currency' },
  { key: 'scenario_count', label: '# Proposals', icon: BarChart3, format: 'number' },
];

const fc = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

function getColorScale(ratio) {
  if (ratio <= 0) return '#f1f5f9';
  if (ratio < 0.15) return '#dcfce7';
  if (ratio < 0.3) return '#86efac';
  if (ratio < 0.5) return '#22c55e';
  if (ratio < 0.7) return '#16a34a';
  if (ratio < 0.85) return '#15803d';
  return '#166534';
}

export default function USStateHeatmap({
  heatmapData,
  expandedSections,
  toggleSection,
  onStateDrillDown
}) {
  const [metric, setMetric] = useState('gross_revenue');
  const [tooltipContent, setTooltipContent] = useState('');

  const stateDataMap = useMemo(() => {
    if (!heatmapData?.heatmap_data) return {};
    const m = {};
    heatmapData.heatmap_data.forEach(d => { m[d.state] = d; });
    return m;
  }, [heatmapData]);

  const maxForMetric = useMemo(() => {
    if (!heatmapData) return 0;
    if (metric === 'gross_revenue') return heatmapData.max_gross_revenue || heatmapData.max_value || 0;
    if (metric === 'net_revenue') return heatmapData.max_net_revenue || 0;
    return heatmapData.max_scenario_count || 0;
  }, [heatmapData, metric]);

  const activeMetric = METRICS.find(m => m.key === metric);

  if (!heatmapData) return null;

  const totalGross = heatmapData.heatmap_data?.reduce((s, d) => s + (d.gross_revenue || 0), 0) || 0;
  const totalNet = heatmapData.heatmap_data?.reduce((s, d) => s + (d.net_revenue || 0), 0) || 0;
  const totalScenarios = heatmapData.heatmap_data?.reduce((s, d) => s + (d.scenario_count || 0), 0) || 0;

  return (
    <div className="glass-card p-6 mt-6" data-testid="us-state-heatmap">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => toggleSection('heatmap')} className="text-slate-400 hover:text-slate-600">
            {expandedSections.heatmap ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
          <MapPin className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-slate-900">US Geographic Heatmap</h3>
          <span className="text-sm text-slate-500">({heatmapData.total_states} states)</span>
        </div>
        {/* Metric Toggle */}
        {expandedSections.heatmap && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="heatmap-metric-toggle">
            {METRICS.map(m => {
              const Icon = m.icon;
              return (
                <button
                  key={m.key}
                  data-testid={`heatmap-metric-${m.key}`}
                  onClick={() => setMetric(m.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    metric === m.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {m.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {expandedSections.heatmap && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">States with Data</p>
              <p className="text-2xl font-bold text-indigo-600">{heatmapData.total_states}</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Total Gross Revenue</p>
              <p className="text-2xl font-bold text-emerald-700">{fc(totalGross)}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Total Net Revenue</p>
              <p className="text-2xl font-bold text-blue-700">{fc(totalNet)}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Total Proposals</p>
              <p className="text-2xl font-bold text-amber-700">{totalScenarios}</p>
            </div>
          </div>

          {/* US Map */}
          <div className="relative bg-slate-50 rounded-xl border border-slate-200 overflow-hidden" style={{ minHeight: 420 }}>
            <ComposableMap
              projection="geoAlbersUsa"
              projectionConfig={{ scale: 1000 }}
              width={800}
              height={500}
              style={{ width: '100%', height: '100%' }}
            >
              <Geographies geography={US_STATES_TOPO}>
                {({ geographies }) =>
                  geographies.map(geo => {
                    const fips = geo.id;
                    const stateCode = FIPS_TO_STATE[fips];
                    const sd = stateDataMap[stateCode];
                    const val = sd ? sd[metric] || 0 : 0;
                    const ratio = maxForMetric > 0 ? val / maxForMetric : 0;
                    const fill = val > 0 ? getColorScale(ratio) : '#f1f5f9';
                    const stateName = STATE_NAMES[stateCode] || stateCode || '';

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke="#94a3b8"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: { fill: '#4f46e5', stroke: '#312e81', strokeWidth: 1.5, outline: 'none', cursor: 'pointer' },
                          pressed: { outline: 'none' },
                        }}
                        onClick={() => {
                          if (stateCode && sd) onStateDrillDown(stateCode);
                        }}
                        onMouseEnter={() => {
                          const html = sd
                            ? `<div class="text-left min-w-[160px]">
                                <div class="font-semibold text-slate-900 mb-1">${stateName} (${stateCode})</div>
                                <div class="text-xs space-y-0.5">
                                  <div>Gross Revenue: <b>${fc(sd.gross_revenue)}</b></div>
                                  <div>Net Revenue: <b>${fc(sd.net_revenue)}</b></div>
                                  <div>Proposals: <b>${sd.scenario_count}</b></div>
                                  <div>Municipalities: <b>${sd.municipality_count}</b></div>
                                </div>
                                <div class="text-[10px] text-indigo-600 mt-1 font-medium">Click to drill down</div>
                              </div>`
                            : `<div class="text-left"><div class="font-semibold">${stateName}</div><div class="text-xs text-slate-400">No data</div></div>`;
                          setTooltipContent(html);
                        }}
                        onMouseLeave={() => setTooltipContent('')}
                        data-tooltip-id="state-heatmap-tooltip"
                        data-tooltip-html={tooltipContent}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-md border border-slate-200">
              <p className="text-[10px] font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">{activeMetric?.label}</p>
              <div className="flex items-center gap-0.5">
                <span className="text-[10px] text-slate-500 mr-1">Low</span>
                {['#dcfce7','#86efac','#22c55e','#16a34a','#15803d','#166534'].map((c,i) => (
                  <div key={i} className="w-6 h-3.5 first:rounded-l last:rounded-r" style={{ backgroundColor: c }} />
                ))}
                <span className="text-[10px] text-slate-500 ml-1">High</span>
              </div>
            </div>

            <Tooltip
              id="state-heatmap-tooltip"
              className="z-50 !rounded-lg"
              style={{
                backgroundColor: 'white',
                color: '#1e293b',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                fontSize: '0.8125rem',
                border: '1px solid #e2e8f0',
              }}
            />
          </div>

          {/* State Rankings Table */}
          {heatmapData.heatmap_data.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">State Rankings</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-200 bg-slate-50">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">#</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">State</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Gross Revenue</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Net Revenue</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Proposals</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Cities</th>
                      <th className="text-center py-2 px-3 font-semibold text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapData.heatmap_data
                      .sort((a, b) => (b[metric] || 0) - (a[metric] || 0))
                      .map((sd, idx) => {
                        const pct = maxForMetric > 0 ? ((sd[metric] || 0) / maxForMetric) * 100 : 0;
                        return (
                          <tr
                            key={sd.state}
                            data-testid={`state-row-${sd.state}`}
                            className="border-b border-slate-100 hover:bg-indigo-50 cursor-pointer transition-colors"
                            onClick={() => onStateDrillDown(sd.state)}
                          >
                            <td className="py-2.5 px-3 text-slate-400 font-mono text-xs">{idx + 1}</td>
                            <td className="py-2.5 px-3">
                              <span className="font-semibold text-slate-800">{STATE_NAMES[sd.state] || sd.state}</span>
                              <span className="ml-1.5 text-slate-400 text-xs">({sd.state})</span>
                            </td>
                            <td className="py-2.5 px-3 text-right font-medium text-emerald-700">{fc(sd.gross_revenue)}</td>
                            <td className="py-2.5 px-3 text-right font-medium text-blue-700">{fc(sd.net_revenue)}</td>
                            <td className="py-2.5 px-3 text-right text-slate-700">{sd.scenario_count}</td>
                            <td className="py-2.5 px-3 text-right text-slate-500">{sd.municipality_count}</td>
                            <td className="py-2.5 px-3 text-center">
                              <div className="w-20 bg-slate-200 rounded-full h-1.5 inline-block">
                                <div className="h-1.5 rounded-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-400 mt-4 text-center">Click any state on the map or table to drill down to city/county level</p>
        </>
      )}
    </div>
  );
}
